/**
 * PlayerSeasonHighlight — auto-generated "brag card" sat below the
 * PlayerPerformanceCharts. Picks the most impressive single fact about the
 * player's season and frames it for sharing with parents / supporters.
 *
 *  • Headline tiers (first match wins):
 *      1. Goals + Assists tally       → "X has N G+A in M matches"
 *      2. Perfect training attendance → "100% training attendance over N sessions"
 *      3. Started every match (3+)    → "Started every match this season"
 *      4. High average rating (>=7.5) → "Avg rating X.X across N matches"
 *      5. Hot streak (G+A in last 3)  → "On a hot streak — N G+A in last 3"
 *      6. Neutral minutes line        → "Played N% of available minutes"
 *
 *  • Share button uses navigator.share() on supported devices (mobile picker
 *    surfaces WhatsApp / Messages / Mail). Falls back to clipboard copy on
 *    desktop with a toast.
 */
import { useMemo } from 'react';
import axios from 'axios';
import { Share2, Star } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function buildHighlight(player, matches, trainingStats) {
  const playerName = (player?.name || 'This player').split(' ')[0] || 'This player';

  const played = (matches || []).filter(
    (m) => m.status === 'result' && (m.was_starter || m.was_sub)
  );

  const totalGoals = played.reduce((sum, m) => sum + Number(m?.stats?.goals || 0), 0);
  const totalAssists = played.reduce((sum, m) => sum + Number(m?.stats?.assists || 0), 0);
  const totalMinutes = played.reduce((sum, m) => sum + Number(m?.stats?.minutes_played || 0), 0);
  const ratings = played
    .map((m) => (m?.stats?.rating != null ? Number(m.stats.rating) : null))
    .filter((r) => r != null && !Number.isNaN(r));
  const avgRating = ratings.length
    ? Math.round((ratings.reduce((a, b) => a + b, 0) / ratings.length) * 10) / 10
    : null;

  const startsCount = played.filter((m) => m.was_starter).length;

  const sessions = trainingStats?.sessions || [];
  const totalSessions = sessions.length;
  const presentSessions = sessions.filter((s) => s.present).length;
  const attendancePct = totalSessions ? Math.round((presentSessions / totalSessions) * 100) : null;

  // Last-3 hot streak
  const last3 = played.slice(0, 3);
  const last3GA = last3.reduce(
    (sum, m) => sum + Number(m?.stats?.goals || 0) + Number(m?.stats?.assists || 0),
    0
  );

  // Build supporting stat strings shared across tiers
  const supportBits = [];
  if (attendancePct != null && totalSessions >= 3) supportBits.push(`${attendancePct}% training attendance`);
  if (startsCount >= 3) supportBits.push(`${startsCount} start${startsCount === 1 ? '' : 's'}`);
  if (avgRating != null) supportBits.push(`avg rating ${avgRating}`);

  // Tier selection
  const ga = totalGoals + totalAssists;
  if (ga >= 3 && played.length >= 2) {
    return {
      tier: 'production',
      headline: (
        <>
          {playerName} has{' '}
          <span className="text-amber-300">{ga} G+A</span> in {played.length} matches.
        </>
      ),
      plain: `${playerName} has ${ga} goals + assists in ${played.length} matches this season (${totalGoals} goal${totalGoals === 1 ? '' : 's'}, ${totalAssists} assist${totalAssists === 1 ? '' : 's'}).`,
      support: supportBits.join(' · '),
    };
  }

  if (attendancePct === 100 && totalSessions >= 4) {
    return {
      tier: 'attendance',
      headline: (
        <>
          {playerName} hasn't missed a training —{' '}
          <span className="text-amber-300">100% attendance</span> across {totalSessions} sessions.
        </>
      ),
      plain: `${playerName} has been to every single training this season — 100% attendance across ${totalSessions} sessions.`,
      support: [
        played.length ? `${played.length} match${played.length === 1 ? '' : 'es'} played` : null,
        avgRating != null ? `avg rating ${avgRating}` : null,
      ].filter(Boolean).join(' · '),
    };
  }

  if (startsCount >= 3 && startsCount === played.length) {
    return {
      tier: 'starter',
      headline: (
        <>
          {playerName} has{' '}
          <span className="text-amber-300">started every match</span> this season ({startsCount}/{startsCount}).
        </>
      ),
      plain: `${playerName} has started every match this season (${startsCount} of ${startsCount}).`,
      support: [
        totalMinutes ? `${totalMinutes} minutes` : null,
        attendancePct != null ? `${attendancePct}% training attendance` : null,
        avgRating != null ? `avg rating ${avgRating}` : null,
      ].filter(Boolean).join(' · '),
    };
  }

  if (avgRating != null && avgRating >= 7.5 && ratings.length >= 3) {
    return {
      tier: 'rating',
      headline: (
        <>
          {playerName} is averaging{' '}
          <span className="text-amber-300">{avgRating}</span> across {ratings.length} match{ratings.length === 1 ? '' : 'es'}.
        </>
      ),
      plain: `${playerName} is averaging a ${avgRating} rating across ${ratings.length} match${ratings.length === 1 ? '' : 'es'} this season.`,
      support: supportBits.join(' · '),
    };
  }

  if (last3GA >= 2 && last3.length === 3) {
    return {
      tier: 'streak',
      headline: (
        <>
          {playerName} is on a{' '}
          <span className="text-amber-300">hot streak</span> — {last3GA} G+A in the last 3 matches.
        </>
      ),
      plain: `${playerName} is on a hot streak — ${last3GA} goals + assists in the last 3 matches.`,
      support: supportBits.join(' · '),
    };
  }

  // Neutral fallback — every player still gets a line
  if (played.length >= 1) {
    return {
      tier: 'neutral',
      headline: (
        <>
          {playerName} has featured in{' '}
          <span className="text-amber-300">{played.length} match{played.length === 1 ? '' : 'es'}</span> this season.
        </>
      ),
      plain: `${playerName} has featured in ${played.length} match${played.length === 1 ? '' : 'es'} this season${totalMinutes ? `, racking up ${totalMinutes} minutes` : ''}.`,
      support: supportBits.join(' · '),
    };
  }

  if (attendancePct != null && totalSessions >= 1) {
    return {
      tier: 'neutral',
      headline: (
        <>
          {playerName} has been to{' '}
          <span className="text-amber-300">{presentSessions} of {totalSessions}</span> training sessions.
        </>
      ),
      plain: `${playerName} has been to ${presentSessions} of ${totalSessions} training sessions (${attendancePct}%).`,
      support: '',
    };
  }

  return null; // Truly nothing to celebrate yet (new player)
}

export function PlayerSeasonHighlight({ player, matches = [], trainingStats = null, teamName }) {
  const highlight = useMemo(
    () => buildHighlight(player, matches, trainingStats),
    [player, matches, trainingStats]
  );

  if (!highlight) return null;

  const teamSuffix = teamName ? ` — ${teamName}` : '';
  const shareText = `${highlight.plain}${teamSuffix}\n\nProud of the effort! 👏`;

  const handleShare = async () => {
    const logShare = () => {
      // Fire-and-forget — never block the share UX on a logging failure
      axios
        .post(`${API}/players/share-events`, {
          reminder_type: 'player_highlight_share',
          metadata: {
            player_id: player?.id,
            tier: highlight?.tier,
          },
        })
        .catch(() => {});
    };
    const payload = {
      title: `${player?.name || 'Player'} · season highlight`,
      text: shareText,
    };
    try {
      if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
        await navigator.share(payload);
        logShare();
        return;
      }
    } catch (err) {
      // User cancelled or share failed; fall through to clipboard.
      if (err && err.name === 'AbortError') return;
    }
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareText);
        toast.success('Highlight copied — paste it into your parent group chat.');
        logShare();
        return;
      }
    } catch (_) {
      // Ignore and fall through to manual fallback.
    }
    // Last-ditch fallback for very old browsers
    window.prompt('Copy this message to share with parents:', shareText);
    logShare();
  };

  return (
    <div
      className="relative overflow-hidden rounded-2xl p-5 sm:p-6 text-white shadow-md"
      style={{
        background:
          'linear-gradient(135deg, #047857 0%, #064E3B 55%, #022c22 100%)',
      }}
      data-testid="player-season-highlight"
    >
      {/* Decorative orbs */}
      <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-white/5 pointer-events-none" />
      <div className="absolute right-6 bottom-6 w-2 h-2 rounded-full bg-amber-300/80 pointer-events-none" />

      <div className="relative flex flex-col sm:flex-row sm:items-start gap-4">
        <div className="w-11 h-11 rounded-full bg-amber-300 text-emerald-900 grid place-items-center shrink-0 shadow-sm">
          <Star className="w-5 h-5" strokeWidth={2.2} fill="currentColor" />
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold tracking-[0.25em] uppercase text-emerald-200">
            Season highlight
          </p>
          <p className="font-title text-xl sm:text-2xl font-bold leading-snug mt-1">
            {highlight.headline}
          </p>
          {highlight.support && (
            <p className="text-emerald-200 text-xs sm:text-sm mt-2">{highlight.support}</p>
          )}
        </div>

        <button
          onClick={handleShare}
          data-testid="player-highlight-share-btn"
          className="shrink-0 inline-flex items-center justify-center gap-2 bg-white text-emerald-800 font-semibold text-sm px-4 py-2.5 rounded-xl shadow-md hover:bg-amber-50 transition-colors w-full sm:w-auto"
        >
          <Share2 className="w-4 h-4" strokeWidth={2} />
          Share to parents
        </button>
      </div>
    </div>
  );
}

export default PlayerSeasonHighlight;
